import type {
  DiscardBookingCopy,
  WizardDangerousGoodsRow,
  WizardFormValues,
  WizardInsuranceCoverageValues,
  WizardPartiesValues,
  WizardRoutingValues,
  WizardServiceOptionValues,
  WizardServicesValues,
  WizardSpecialHandlingValues,
  WizardStepConfig
} from './types';
import {
  CargoHandlingType,
  CoreBatteries,
  CoreBookingAdditionalServiceOption,
  Currency,
  LooseCargoVariant,
  TemperatureUnit,
  WizardStep
} from './types';

export const wizardSteps: WizardStepConfig[] = [
  {
    id: WizardStep.TERMS,
    label: 'Terms',
    subtitle:
      'Establish the commercial and regional context for this shipment. Setting these details now ensures you only see the steps relevant to your specific journey.',
    title: 'Start your booking'
  },
  {
    id: WizardStep.CARGO,
    label: 'Cargo',
    subtitle: 'Select the transport mode of choice and configure your cargo.',
    title: 'Configure your shipping setup'
  },
  {
    id: WizardStep.ROUTING,
    label: 'Routing',
    subtitle: 'Choose the start and end points and set the timing that fits your logistics plan.',
    title: 'Plan your route and schedule'
  },
  { id: WizardStep.SERVICES, label: 'Services', subtitle: 'Select additional services for this booking.', title: 'Services' },
  { id: WizardStep.PARTIES, label: 'Parties', subtitle: 'Add the parties involved in this booking.', title: 'Parties' },
  {
    id: WizardStep.INSTRUCTIONS,
    label: 'Instructions',
    subtitle: 'Include any additional instructions for this booking.',
    title: 'Provide supporting instructions'
  },
  {
    id: WizardStep.REVIEW,
    label: 'Review',
    subtitle: 'Check the details below and make any final changes before placing your booking.',
    title: 'Review & confirm your booking'
  }
];

export const discardBookingCopy: DiscardBookingCopy = {
  description: 'All the information you have entered will be lost. This cannot be undone.',
  header: 'Discard this booking?',
  leaveLabel: 'Discard booking',
  stayLabel: 'Keep editing'
};

export const emptyDangerousGoodsRow: WizardDangerousGoodsRow = {
  imoClass: null,
  unNumber: ''
};

export const initialSpecialHandlingValues: WizardSpecialHandlingValues = {
  additionalInstructions: '',
  batteryPackingOptions: [],
  batteryType: null,
  containsBatteries: false,
  containsDangerousGoods: false,
  dangerousGoodsRows: [emptyDangerousGoodsRow],
  gensetInlandTransport: null,
  hasAdditionalInstructions: false,
  monitoringLevel: null,
  powerRequirements: null,
  requiresTemperatureControl: false,
  restrictedStacking: false,
  stackable: null,
  temperatureMax: { unit: TemperatureUnit.CELSIUS, value: null },
  temperatureMin: { unit: TemperatureUnit.CELSIUS, value: null },
  temperatureTarget: { unit: TemperatureUnit.CELSIUS, value: null }
};

export const batteryTypeLabelMapping: Record<CoreBatteries, string> = {
  [CoreBatteries.LITHIUM]: 'Lithium',
  [CoreBatteries.LITHIUM_AND_NON_LITHIUM]: 'Both',
  [CoreBatteries.NON_LITHIUM]: 'Non-lithium'
};

export const additionalServiceOptionLabelMapping: Record<CoreBookingAdditionalServiceOption, string> = {
  [CoreBookingAdditionalServiceOption.DESTINATION_ONLY]: 'Destination only',
  [CoreBookingAdditionalServiceOption.ORIGIN_AND_DESTINATION]: 'Origin & destination',
  [CoreBookingAdditionalServiceOption.ORIGIN_ONLY]: 'Origin only'
};

export const initialServiceOptionValues: WizardServiceOptionValues = {
  enabled: false,
  option: CoreBookingAdditionalServiceOption.ORIGIN_AND_DESTINATION
};

export const initialInsuranceCoverageValues: WizardInsuranceCoverageValues = {
  currency: Currency.GBP,
  enabled: false,
  value: null
};

export const initialServicesValues: WizardServicesValues = {
  customsClearance: { ...initialServiceOptionValues },
  haulageAndTrucking: { ...initialServiceOptionValues },
  insuranceCoverage: { ...initialInsuranceCoverageValues },
  storageAndWarehousing: { ...initialServiceOptionValues }
};

export const initialRoutingValues: WizardRoutingValues = {
  cargoReadyDate: null,
  collectionLocation: null,
  deliveryLocation: null,
  dischargeTerminal: null,
  freightContractId: null,
  isFinalDestinationUnknown: false,
  loadingTerminal: null,
  requestedCollectionDate: null,
  requestedCollectionDateTouched: false,
  requiredDeliveryDate: null
};

export const initialPartiesValues: WizardPartiesValues = {
  additionalNotifyParty: null,
  forwarder: null,
  overrides: {},
  references: {},
  sameAsConsignee: {
    bookingParty: false,
    importerOfRecord: true,
    notifyParty: false
  }
};

export const initialWizardFormValues: WizardFormValues = {
  cargo: {
    cargoMode: null,
    handlingType: CargoHandlingType.GENERAL,
    items: [],
    itemsByCargoMode: {},
    looseVariant: LooseCargoVariant.ITEMISED,
    specialHandling: initialSpecialHandlingValues,
    specialHandlingAutoEnabled: false,
    specialHandlingDiffersPerItem: false,
    temperatureControlAutoEnabled: false
  },
  instructions: { specialInstructions: '' },
  parties: initialPartiesValues,
  routing: initialRoutingValues,
  services: initialServicesValues,
  setup: { accountId: null, accountName: null, incoterm: null, modeOfTransport: null, paymentTerms: null, pricing: null }
};
